import React, { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { List } from "react-window";
import { DownloadIcon, LucidLoader, PrinterIcon } from "../Icons";
import { toast } from "react-toastify";

// PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = "https://8xp-ndcs-public-assets.s3.us-east-1.amazonaws.com/pdf.worker.min.js";

/* --------------------------------------------------
   LRU CACHE
-------------------------------------------------- */
class LRUCache {
  constructor(max = 60) {
    this.max = max;
    this.map = new Map();
  }
  get(k) {
    if (!this.map.has(k)) return null;
    const v = this.map.get(k);
    this.map.delete(k);
    this.map.set(k, v);
    return v;
  }
  set(k, v) {
    if (this.map.has(k)) this.map.delete(k);
    this.map.set(k, v);
    if (this.map.size > this.max) {
      const first = this.map.keys().next().value;
      this.map.delete(first);
    }
  }
}

/* --------------------------------------------------
   SEMAPHORE — LIMIT PARALLEL PAGE RENDERS
-------------------------------------------------- */
class Semaphore {
  constructor(limit = 1) {
    this.limit = limit;
    this.active = 0;
    this.queue = [];
  }
  acquire() {
    return new Promise((resolve) => {
      const tryTake = () => {
        if (this.active < this.limit) {
          this.active++;
          resolve(() => {
            this.active--;
            if (this.queue.length) this.queue.shift()();
          });
        } else {
          this.queue.push(tryTake);
        }
      };
      tryTake();
    });
  }
}

/* --------------------------------------------------
   PDF PAGE COMPONENT
-------------------------------------------------- */
const PDFPage = ({ index, pdf, scale, cache, semaphore }) => {
  const canvasRef = useRef(null);
  const pageNum = index + 1;
  const [rendering, setRendering] = useState(false);

  const renderPage = useCallback(
    async (canvas) => {
      if (!pdf || !canvas) return;

      // Cache check
      const cached = cache.get(pageNum);
      if (cached?.dataUrl) {
        const img = new Image();
        img.onload = () => {
          canvas.width = cached.w;
          canvas.height = cached.h;
          canvas.style.width = cached.w / devicePixelRatio + "px";
          canvas.style.height = cached.h / devicePixelRatio + "px";
          canvas.getContext("2d").drawImage(img, 0, 0);
        };
        img.src = cached.dataUrl;
        return;
      }

      setRendering(true);

      try {
        const release = await semaphore.acquire();

        const page = await pdf.getPage(pageNum);
        const ratio = window.devicePixelRatio || 1;

        const effectiveScale = Math.max(scale, 1);
        const viewport = page.getViewport({ scale: effectiveScale });

        const ctx = canvas.getContext("2d");
        canvas.width = viewport.width * ratio;
        canvas.height = viewport.height * ratio;

        canvas.style.width = viewport.width * (scale / effectiveScale) + "px";
        canvas.style.height = viewport.height * (scale / effectiveScale) + "px";

        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

        await page.render({ canvasContext: ctx, viewport }).promise;

        // Cache page
        cache.set(pageNum, {
          dataUrl: canvas.toDataURL("image/png"),
          w: canvas.width,
          h: canvas.height,
        });

        release();
      } finally {
        setRendering(false);
      }
    },
    [pdf, scale, cache, semaphore, pageNum]
  );

  useEffect(() => {
    if (canvasRef.current) renderPage(canvasRef.current);
  }, [renderPage]);

  return (
    <div className="relative bg-white rounded shadow-md p-4 flex justify-center">
      {rendering && (
        <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
          <LucidLoader additionalClass="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      )}
      <canvas ref={canvasRef} />
    </div>
  );
};

/* --------------------------------------------------
   MAIN PDF VIEWER
-------------------------------------------------- */
const PDFModalViewer = ({ show, handleClose, pdfUrl, fileName = "Document.pdf" }) => {
  const [pdf, setPdf] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageHeight, setPageHeight] = useState(1000);
  const [scale, setScale] = useState(1.4);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const PAGE_SPACING = 24; // ⬅⬅⬅ PAGE GAP HERE (controls spacing between pages)

  const cacheRef = useRef(new LRUCache(60));
  const semaphoreRef = useRef(new Semaphore(2));
  const listRef = useRef(null);

  /* --------------------------------------------------
     RESET VIEW STATE WHEN PDF URL CHANGES
  -------------------------------------------------- */
  useEffect(() => {
    setPdf(null);
    setNumPages(0);
    setLoading(true);

    setScale(1.4);
    setPageHeight(1000);

    cacheRef.current = new LRUCache(60);
    semaphoreRef.current = new Semaphore(2);

    if (listRef.current?.scrollToRow) {
      listRef.current.scrollToRow({ index: 0, align: "start" });
    }
  }, [pdfUrl]);

  /* --------------------------------------------------
     LOAD PDF
  -------------------------------------------------- */
  useEffect(() => {
    if (!show) return;

    const load = async () => {
      setLoading(true);

      const task = pdfjsLib.getDocument(pdfUrl);
      const doc = await task.promise;

      setPdf(doc);
      setNumPages(doc.numPages);

      const firstPage = await doc.getPage(1);
      const vp = firstPage.getViewport({ scale: 1 });
      setPageHeight(vp.height);

      setLoading(false);
    };

    load();
  }, [show, pdfUrl]);

  /* --------------------------------------------------
     DOWNLOAD
  -------------------------------------------------- */
  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const blob = await fetch(pdfUrl).then((r) => r.blob());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);

    } catch (error) {
      toast.error('Failed to download file');

    } finally {
      setIsDownloading(false);
    }

  };

  /* --------------------------------------------------
     PRINT
  -------------------------------------------------- */
  const handlePrint = async () => {
    const isFirefox =
      navigator.userAgent.toLowerCase().includes("firefox") &&
      !navigator.userAgent.toLowerCase().includes("seamonkey");


    if (isFirefox) {
      alert("Printing is not supported in Firefox browser. Please use a different browser to print this document.");
      return;
    }
    const blob = await fetch(pdfUrl).then((r) => r.blob());
    const url = URL.createObjectURL(blob);

    const newWindow = window.open(url, "_blank");

    if (newWindow) {
      newWindow.onload = () => {
        newWindow.focus();
        newWindow.print();
      };
    }
  };

  /* --------------------------------------------------
     ROW RENDERER
  -------------------------------------------------- */
  const Row = ({ index, style, ariaAttributes }) => (
    <div
      style={{
        ...style,
        display: "flex",
        justifyContent: "center",
        paddingBottom: PAGE_SPACING,
      }}
      {...ariaAttributes}
    >
      <PDFPage
        index={index}
        pdf={pdf}
        scale={scale}
        cache={cacheRef.current}
        semaphore={semaphoreRef.current}
      />
    </div>
  );

  /* --------------------------------------------------
     ROW HEIGHT (VERY IMPORTANT)
     Must include spacing to prevent clipping
  -------------------------------------------------- */
  const rowHeight = () => pageHeight * scale + 40 + PAGE_SPACING;

  if (!show) return null;

  return (
    <div className="fixed inset-0  bg-black/60 backdrop-blur-lg z-[10002] flex flex-col">

      {/* HEADER */}
      <div className="bg-gray-900  text-white px-6 py-3 flex justify-between items-center">
        <span className="text-lg font-semibold">{fileName}</span>

        <div className="flex items-center gap-3">
          {!isDownloading ? <button onClick={handleDownload} className="px-3 py-1 border border-gray-600 rounded hover:bg-gray-800 flex items-center gap-2">
            <DownloadIcon className="w-4 h-4" /> Save
          </button> : <button className="px-3 py-1 border border-gray-600 rounded hover:bg-gray-800 flex items-center gap-2">
            <LucidLoader />
          </button>}
          <button onClick={handlePrint} className="px-3 py-1 border border-gray-600 rounded hover:bg-gray-800 flex items-center gap-2">
            <PrinterIcon className="w-4 h-4" /> Print
          </button>
          <button onClick={handleClose} className="px-3 py-1 border border-gray-600 rounded hover:bg-gray-800">
            Close
          </button>
        </div>
      </div>

      {/* BODY */}
      <div className="flex-1 bg-gray-100 p-6">
        {loading ? (
          <div className="flex flex-col justify-center items-center gap-3 h-full text-gray-500">
            <LucidLoader className="w-10 h-10 text-blue-500 animate-spin" />
            Loading PDF...
          </div>
        ) : (
          <List
            className="w-full"
            rowCount={numPages}
            rowHeight={rowHeight}
            rowComponent={Row}
            rowProps={{}}
            overscanCount={3}
            defaultHeight={600}
            style={{ height: "80vh" }}
            listRef={listRef}
          />
        )}
      </div>

      {/* FOOTER */}
      <div className="bg-gray-900 text-white px-6  border-t border-gray-700 flex justify-between">
        <span className="flex items-center justify-center">Total Pages: {loading ? <LucidLoader /> : numPages}</span>

        {/* <div className="flex items-center gap-3">
          <button onClick={() => setScale((s) => Math.max(0.4, s - 0.2))} className="px-3 py-1 bg-gray-800 rounded">
            –
          </button>
          <span>{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale((s) => s + 0.2)} className="px-3 py-1 bg-gray-800 rounded">
            +
          </button>
        </div> */}
      </div>

    </div>
  );
};

export default PDFModalViewer;
